import type {
	DeepPartial,
	NubeSDKRuntimeState,
} from "@tiendanube/nube-sdk-internal-types";

export type NubeSDKAppScript = { id: string; script: string };

function mapApps(apps: NubeSDKAppScript[]): Record<string, NubeSDKAppScript> {
	return apps.reduce(
		(acc, app) => {
			acc[String(app.id)] = { id: String(app.id), script: app.script };
			return acc;
		},
		{} as Record<string, NubeSDKAppScript>,
	);
}

export type BootstrapProps = {
	hostId: string;
	initialState: DeepPartial<NubeSDKRuntimeState>;
	apps: NubeSDKAppScript[];
};

export function Bootstrap({ hostId, initialState, apps }: BootstrapProps) {
	try {
		const jsonInitialState = JSON.stringify(initialState);
		const jsonApps = JSON.stringify(mapApps(apps));

		return (
			<script
				// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
				dangerouslySetInnerHTML={{
					__html: `
((global, url, name, scope, state, apps) => {
  const methods = ["on", "off", "send"];
  const initial = { ...state, location: { ...(state.location || {}), url } };
  global[name] = methods.reduce((sdk, method) => {
    sdk[method] = (...args) => {
      sdk.queue.push([method, ...args]);
    };
    return sdk;
  }, { queue: [] });
  global[name].getState = () => initial;
  global[name].send(scope, "init", () => initial);
  const loadMethod = () => {
    if (apps) {
      global[name].send(scope, "app:register", () => ({ apps }));
    }
  };
  if (document.readyState === "complete") {
    setTimeout(loadMethod);
  } else {
    window.addEventListener("load", loadMethod);
  }
})(window, document.location.href, "nubeSDK", "${hostId}", ${jsonInitialState}, ${jsonApps});`,
				}}
				data-testid="nube-sdk-bootstrap"
			/>
		);
	} catch (error) {
		console.error("Failed to initialize NubeSDK:", error);
		return (
			<script
				// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
				dangerouslySetInnerHTML={{
					__html:
						"console.error('Failed to initialize NubeSDK bootstrap script');",
				}}
				data-testid="nube-sdk-bootstrap"
			/>
		);
	}
}
